<template>
  <div class="flex w-full justify-start">
    <div class="flex w-full max-w-[92%] gap-3 sm:max-w-[80%]">
      <div
        class="mt-1 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-iris-600 text-white shadow-sm dark:bg-iris-500">
        <Icon icon="heroicons:rocket-launch" class="size-5" />
      </div>
      <div
        class="min-w-0 flex-1 rounded-2xl rounded-tl-sm border border-iris-200 bg-iris-50/80 px-5 py-4 shadow-sm dark:border-iris-800 dark:bg-iris-950/30">
        <!-- The ask itself is the assistant message right above this card. -->
        <p class="text-[15px] font-medium leading-relaxed text-charcoal-900 dark:text-charcoal-100">
          Boost this Request!
        </p>

        <p v-if="request.field_lens_name"
          class="mt-1 text-xs text-charcoal-500 dark:text-charcoal-400">{{ request.field_lens_name }}</p>

        <Stack full marginTop="medium" gap="medium">
          <Text size="small" color="light">
            {{ selectorText }}
          </Text>

          <Box full class="relative" noWrap>
            <Select
              v-model="selectedSearchType"
              :options="searchTypeOptions"
              optionLabel="label"
              :optionValue="null"
              :disabled="disabled"
              class="mr-3 self-center"
              size="large"
            >
              <template #options="{ option }">
                <Box alignItems="center" justifyContent="center">
                  <Icon :icon="option.icon" class="size-5" :class="option.color" />
                </Box>
              </template>

              <template #input="{ value }">
                <Box alignItems="center">
                  <Box v-if="value">
                    <Icon :icon="value.icon" class="size-5" :class="value.color" />
                  </Box>
                  <Box v-else>
                    <Text size="small">Search Type</Text>
                  </Box>
                </Box>
              </template>
            </Select>

            <AutoComplete
              v-if="selectedSearchType?.label === 'Account'"
              v-model="selectedAccount"
              :disabled="disabled"
              showClear
              optionLabel="name"
              :apiSearch="searchAccountsByKeyword"
              :delay="500"
              placeholder="Search Accounts..."
              class="w-full"
            />

            <AutoComplete
              v-if="selectedSearchType?.label === 'Opportunity'"
              v-model="selectedOpportunity"
              :disabled="disabled"
              optionLabel="name"
              :apiSearch="searchOpportunitiesByKeyword"
              :delay="500"
              showClear
              class="w-full"
              placeholder="Search Opportunities..."
            />

            <AutoComplete
              v-if="selectedSearchType?.label === 'SE Support Case'"
              v-model="selectedSupportCase"
              :disabled="disabled"
              :apiSearch="searchSupportCasesByKeyword"
              optionLabel="subject"
              :delay="500"
              showClear
              class="w-full"
              placeholder="Search Support Cases..."
            />
          </Box>

          <Box v-if="selectedSearchType?.label === 'Account' && boostAccountDisplay.id">
            <Stack class="h-34 w-full flex-col rounded-md bg-white border border-charcoal-200 dark:border-charcoal-800 dark:bg-charcoal-900">
              <Box v-if="!fetchAdditionalDataLoading" marginLeft="medium" paddingY="small">
                <Box alignItems="center" full noWrap>
                  <Text size="small" class="truncate">{{ boostAccountDisplay.name }}</Text>
                  <SalesforceIcon class="size-5 ml-2 self-center" :objectId="boostAccountDisplay.id"/>
                </Box>
                <Stack marginTop="xsmall" gap="xsmall">
                  <Stack>
                    <Text color="light" size="xsmall">Account Tier</Text>
                    <Box>
                      <Icon icon="mdi:podium" class="h-3 w-3 mr-1"/>
                      <Text size="xsmall" class="whitespace-pre">{{ boostAccountDisplay.tier || '' }}</Text>
                    </Box>
                  </Stack>
                  <Stack>
                    <Text color="light" size="xsmall">Account Type</Text>
                    <Box>
                      <Icon icon="heroicons:building-office" class="h-3 w-3 mr-1 mt-0.5"/>
                      <Text size="xsmall">{{ boostAccountDisplay.type || '' }}</Text>
                    </Box>
                  </Stack>
                </Stack>
                <Stack marginTop="xsmall" gap="xsmall" marginLeft="xxlarge">
                  <Stack>
                    <Text color="light" size="xsmall">ACV Pipeline</Text>
                    <Text size="xsmall" class="whitespace-pre">{{ formatMoneyCompact(boostAccountDisplay.currentOpenACVPipeline) || '$0' }}</Text>
                  </Stack>
                  <Stack>
                    <Text color="light" size="xsmall">Total Bookings</Text>
                    <Text size="xsmall" class="whitespace-pre">{{ formatMoneyCompact(boostAccountDisplay.totalBookings) || '$0' }}</Text>
                  </Stack>
                </Stack>
              </Box>
              <Box v-else class="w-full h-34" justifyContent="center" alignItems="center">
                <Loader color="#bbb" :size='32'></Loader>
              </Box>
            </Stack>
          </Box>

          <Box v-if="selectedSearchType?.label === 'Opportunity' && boostOpportunityDisplay.id">
            <Stack class="w-full flex-col rounded-md bg-white border border-charcoal-200 dark:border-charcoal-800 dark:bg-charcoal-900">
              <Box v-if="!fetchAdditionalDataLoading" marginLeft="medium" paddingY="small">
                <Box alignItems="center" full noWrap>
                  <Text size="small" class="truncate">{{ boostOpportunityDisplay.name }}</Text>
                  <SalesforceIcon class="size-5 ml-2 self-center" :objectId="boostOpportunityDisplay.id"/>
                </Box>
                <Box full alignItems="center">
                  <Icon icon="heroicons:building-office" class="h-3 w-3 mr-1"></Icon>
                  <Text color="light" size="small">{{ boostOpportunityDisplay.account?.name }}</Text>
                </Box>
                <Stack marginTop="xsmall" gap="xsmall">
                  <Stack>
                    <Text color="light" size="xsmall">Close Date</Text>
                    <Box alignItems="center">
                      <Icon icon="heroicons:calendar" class="h-3 w-3 mr-1"/>
                      <Text size="xsmall">{{ boostOpportunityDisplay.details?.closeDate }}</Text>
                    </Box>
                  </Stack>
                </Stack>
                <Stack marginTop="xsmall" gap="xsmall" marginLeft="xxlarge">
                  <Stack>
                    <Text color="light" size="xsmall">ACV Amount</Text>
                    <Text size="xsmall" class="whitespace-pre">{{ formatMoneyCompact(boostOpportunityDisplay.details?.acvAmount) || '$0' }}</Text>
                  </Stack>
                </Stack>
              </Box>
              <Box v-else class="w-full h-26" justifyContent="center" alignItems="center">
                <Loader color="#bbb" :size='32'></Loader>
              </Box>
            </Stack>
          </Box>

          <Box v-if="selectedSearchType?.label === 'SE Support Case' && selectedSupportCase">
            <Stack class="w-full flex-col rounded-md bg-white border border-charcoal-200 dark:border-charcoal-800 dark:bg-charcoal-900">
              <Box v-if="!fetchAdditionalDataLoading" marginLeft="medium" paddingY="small">
                <Box alignItems="center" full noWrap class="mb-[2px]">
                  <Text size="small" class="truncate">{{ selectedSupportCase.subject }}</Text>
                  <SalesforceIcon class="size-5 ml-2 self-center" :objectId="selectedSupportCase.id"/>
                </Box>
                <Box v-if="supportCaseAdditionalDetails.accountObject" full alignItems="center">
                  <Icon icon="heroicons:building-office" class="h-3 w-3 mr-1"></Icon>
                  <Text color="light" size="xsmall" class="truncate">{{ supportCaseAdditionalDetails.accountObject.name }}</Text>
                </Box>
                <Box v-if="supportCaseAdditionalDetails.opportunityObject" full alignItems="center">
                  <Icon icon="heroicons:sparkles" class="h-3 w-3 mr-1"></Icon>
                  <Text color="light" size="xsmall" class="truncate">{{ supportCaseAdditionalDetails.opportunityObject.name }}</Text>
                </Box>
                <Stack marginTop="xsmall" gap="xsmall">
                  <Stack>
                    <Text color="light" size="xsmall">Created Date</Text>
                    <Box alignItems="center">
                      <Icon icon="heroicons:calendar" class="h-3 w-3 mr-1"/>
                      <Text size="xsmall">{{ supportCaseAdditionalDetails.createdDate ? prettyDate(supportCaseAdditionalDetails.createdDate) : '' }}</Text>
                    </Box>
                  </Stack>
                </Stack>
                <Stack marginTop="xsmall" gap="xsmall" marginLeft="xxlarge">
                  <Stack>
                    <Text color="light" size="xsmall">Status</Text>
                    <Text size="xsmall" class="whitespace-pre">{{ supportCaseAdditionalDetails.status }}</Text>
                  </Stack>
                </Stack>
              </Box>
              <Box v-else class="w-full h-26" justifyContent="center" alignItems="center">
                <Loader color="#bbb" :size='32'></Loader>
              </Box>
            </Stack>
          </Box>
        </Stack>

        <Text size="small" marginTop="medium" marginBottom="small" color="light">Explain why this request matters to your customer.</Text>

        <Textarea
          v-model="justification"
          label="Justification"
          rows="5"
          variant="in"
          size="small"
          :disabled="disabled"
          class="w-full mb-4"
        />

        <Box gap="small">
          <Button size="sm" color="purple" :disabled="disabled || !validForSubmit" @click="submit()">
            Submit
          </Button>
          <Button size="sm" :disabled="disabled" @click="cancel()">
            Cancel
          </Button>
        </Box>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref, watch } from 'vue'
import { Icon } from '@iconify/vue'
import qs from 'qs'
import { uniqBy } from 'lodash'
import { v2 } from '@/services/api.js'
import { formatMoneyCompact, prettyDate } from '@/js/utils.js'
import { buildBoostAnswer } from '@/components/Agent/boostAnswer.js'
import { Box, Stack } from '@/components/Core/Layout'
import Button from '@/components/Core/Button.vue'
import Loader from '@/components/Core/Loader.vue'
import SalesforceIcon from '@/components/Core/SalesforceIcon.vue'
import Text from '@/components/Core/Text.vue'
import AutoComplete from '@/components/Core/Form/AutoComplete.vue'
import Select from '@/components/Core/Form/Select.vue'
import Textarea from '@/components/Core/Form/Textarea.vue'

const props = defineProps({
  request: {
    type: Object,
    required: true,
  },
  disabled: {
    type: Boolean,
    default: false,
  },
})

const emit = defineEmits(['answer'])

const SEARCH_TYPES = [
  { label: 'Opportunity', icon: 'heroicons:sparkles', color: 'text-[#FCB95B]' },
  { label: 'SE Support Case', icon: 'heroicons:briefcase', color: 'text-[#6ac312]' },
  { label: 'Account', icon: 'heroicons:building-office', color: 'text-[#7F8DE1]' }
]

const selectedAccount = ref(null)
const selectedOpportunity = ref(null)
const selectedSupportCase = ref(null)
const accountAdditionalDetails = ref({})
const opportunityAdditionalDetails = ref({})
const supportCaseAdditionalDetails = ref({})
const fetchAdditionalDataLoading = ref(false)
const justification = ref(null)

const searchTypeOptions = computed(() => {
  // Mirrors searchTypeOptions() in FieldLens.vue: Third-Party App Validation boosts
  // can't be tied to an Account.
  const types = props.request?.field_lens_type === 'Third-Party App Validation'
    ? SEARCH_TYPES.filter(type => type.label !== 'Account')
    : SEARCH_TYPES

  const allowed = props.request?.allowed_types

  if (!Array.isArray(allowed) || !allowed.length) {
    return types
  }

  const filtered = types.filter(type => allowed.includes(type.label))

  return filtered.length ? filtered : types
})

const selectedSearchType = ref(searchTypeOptions.value[0])

const boostAccountDisplay = computed(() => {
  return { ...selectedAccount.value, ...accountAdditionalDetails.value }
})

const boostOpportunityDisplay = computed(() => {
  return { ...selectedOpportunity.value, ...opportunityAdditionalDetails.value }
})

const selectorText = computed(() => {
  const labels = searchTypeOptions.value.map(type => type.label)

  if (labels.length === 1) {
    return `Select a related ${labels[0]}.`
  }

  return `Select a related ${labels.slice(0, -1).join(', ')}, or ${labels.at(-1)}.`
})

const selectedRecord = computed(() => {
  if (selectedSearchType.value?.label === 'Account') return selectedAccount.value
  if (selectedSearchType.value?.label === 'Opportunity') return selectedOpportunity.value
  return selectedSupportCase.value
})

const validForSubmit = computed(() => Boolean(selectedRecord.value?.id && justification.value))

async function searchAccountsByKeyword(options, query) {
  if (!query) {
    return options
  }

  const accounts = (await v2.get(`/accounts?filter[search]=${query}`)).data

  return uniqBy([...options, ...accounts], 'id')
}

async function searchOpportunitiesByKeyword(options, query) {
  if (!query) {
    return options
  }

  const results = (await v2.get(`/opportunities?filter[search]=${query}`)).data
    .filter(opp => !opp.techCloseStatus?.includes('Closed'))

  return uniqBy([...options, ...results], 'id')
}

async function searchSupportCasesByKeyword(options, query) {
  if (!query) {
    return options
  }

  const results = (await v2.get('/se-support-cases', {
    params: {
      filter: {
        search: query,
        type: props.request?.field_lens_type
      }
    },
    paramsSerializer: (params) => {
      return qs.stringify(params, { arrayFormat: 'brackets' })
    }
  })).data

  return uniqBy([...options, ...results], 'id')
}

async function fetchAdditionalAccountData(accountId) {
  accountAdditionalDetails.value = {}

  if (!accountId) {
    return
  }

  fetchAdditionalDataLoading.value = true

  try {
    const { data } = await v2.get(`/accounts/${accountId}/technical-checkin-summary`)
    accountAdditionalDetails.value = data
  }
  catch(err) {
    console.error(err)
  }
  finally {
    fetchAdditionalDataLoading.value = false
  }
}

async function fetchAdditionalOpportunityData(opportunityId) {
  opportunityAdditionalDetails.value = {}

  if (!opportunityId) {
    return
  }

  fetchAdditionalDataLoading.value = true

  try {
    const { data } = await v2.get(`/opportunities/${opportunityId}/field-lens`)
    opportunityAdditionalDetails.value = data
  }
  catch(err) {
    console.error(err)
  }
  finally {
    fetchAdditionalDataLoading.value = false
  }
}

async function fetchAdditionalSupportCaseData(supportCase) {
  supportCaseAdditionalDetails.value = {}

  if (!supportCase) {
    return
  }

  fetchAdditionalDataLoading.value = true

  try {
    let accountObject = null
    if (supportCase.accountId) {
      const { data } = await v2.get(`/accounts/${supportCase.accountId}`)
      accountObject = data
    }

    let opportunityObject = null
    if (supportCase.opportunity) {
      const { data } = await v2.get(`/opportunities/${supportCase.opportunity}`)
      opportunityObject = data
    }

    supportCaseAdditionalDetails.value = { ...supportCase, accountObject, opportunityObject }
  }
  catch(err) {
    console.error(err)
  }
  finally {
    fetchAdditionalDataLoading.value = false
  }
}

// Mirrors resolveBoostSelection() in FieldLens.vue: an opportunity or support case
// also identifies the account the boost belongs to.
function resolveSelection() {
  if (selectedSearchType.value?.label === 'Account') {
    return { account: selectedAccount.value, opportunity: null, supportCase: null }
  }

  if (selectedSearchType.value?.label === 'Opportunity') {
    return {
      account: boostOpportunityDisplay.value.account ?? null,
      opportunity: selectedOpportunity.value,
      supportCase: null
    }
  }

  return {
    account: supportCaseAdditionalDetails.value.accountObject ?? null,
    opportunity: supportCaseAdditionalDetails.value.opportunityObject ?? null,
    supportCase: selectedSupportCase.value
  }
}

function submit() {
  if (props.disabled || !validForSubmit.value) return

  const { account, opportunity, supportCase } = resolveSelection()
  const selectionType = selectedSearchType.value?.label

  // Emit only the object the user actually picked. The persistence tool derives
  // the parent account/opportunity itself, and passing more than one id would
  // trip its "exactly one of" guard.
  let selectionId = null
  if (selectionType === 'SE Support Case') selectionId = supportCase?.id
  else if (selectionType === 'Opportunity') selectionId = opportunity?.id
  else if (selectionType === 'Account') selectionId = account?.id

  if (!selectionId) return

  emit('answer', buildBoostAnswer({
    fieldLensId: props.request?.field_lens_id ?? null,
    fieldLensName: props.request?.field_lens_name ?? null,
    selectionType,
    selectionId,
    justification: justification.value
  }))
}

function cancel() {
  if (props.disabled) return

  emit('answer', 'Cancelled the boost request.')
}

watch(selectedAccount, account => fetchAdditionalAccountData(account?.id))
watch(selectedOpportunity, opportunity => fetchAdditionalOpportunityData(opportunity?.id))
watch(selectedSupportCase, supportCase => fetchAdditionalSupportCaseData(supportCase))

watch(selectedSearchType, type => {
  if (type?.label !== 'Account') selectedAccount.value = null
  if (type?.label !== 'Opportunity') selectedOpportunity.value = null
  if (type?.label !== 'SE Support Case') selectedSupportCase.value = null
})

watch(searchTypeOptions, options => {
  if (!options.some(option => option.label === selectedSearchType.value?.label)) {
    selectedSearchType.value = options[0]
  }
})
</script>
